import {
  IError,
  IFilledFormV2,
  IGizmo,
  IIndex,
  IRootGizmoData,
  IUserV2,
} from '../types';
import { IDatabase } from '../database';
import {
  columnSearch,
  getBatchIndex,
  getFormSignup,
  getIndex,
  getLocations,
  getRootGizmoConfigs,
  getRootGizmoData,
  getRootGizmoDataPdf,
  getSystemIndex,
  removeRootGizmoData,
  saveRootGizmoData,
  searchFilledForms,
  serverScript,
  serverValidation,
  sync,
  textSearch,
} from './functions';
import { FlowyFoundation, FlowyGlobal } from '../flowy';
import { Gizmo } from '../gizmos';

interface IConstructor {
  apiUrl: string;
  user?: IUserV2;
  database?: IDatabase;
  global?: FlowyGlobal;
  foundation?: FlowyFoundation;
}

/**
 * Data Access Object
 */
class Dao {
  /**
   * URL of the server API
   */
  private _apiUrl: string;
  /**
   * Object with the user's information
   */
  private _user?: IUserV2;
  /**
   * Database instance
   */
  private _database?: IDatabase;
  private _global?: FlowyGlobal;
  /**
   * Flowy Foundation instance
   */
  private _foundation?: FlowyFoundation;

  constructor({ apiUrl, user, database, global, foundation }: IConstructor) {
    this._apiUrl = apiUrl;
    this._user = user;
    this._database = database;
    this._global = global;
    this._foundation = foundation;
  }

  public init = (): Promise<Dao> => {
    return new Promise<Dao>(async resolve => {
      resolve(this);
    });
  };

  public getApiUrl = (): string => {
    return this._apiUrl;
  };

  /**
   * Get the authenticated user
   * @returns {IUserV2}
   */
  public getUser = (): IUserV2 | undefined => {
    return this._user;
  };

  /**
   * Get the database instance
   * @returns {IDatabase}
   */
  public getDatabase = (): IDatabase | undefined => {
    return this._database;
  };

  public sync = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      await sync({
        // TODO: we're missing how we're going to handle public forms
        // publicRG: this._
        context: {
          apiUrl: this._apiUrl,
          user: this._user,
          database: this._database,
          global: this._global,
          foundation: this._foundation,
        },
      });
      resolve();
    });
  };

  public getIndex = ({
    type,
    fid,
    page,
    pageSize,
    gizmo,
    gizmoConfig,
  }: IGetIndexParams): Promise<IIndex | undefined> => {
    return new Promise<IIndex | undefined>(async resolve => {
      if (this._user) {
        resolve(
          getIndex({
            type,
            fid,
            page,
            pageSize,
            gizmo,
            gizmoConfig,
            context: {
              apiUrl: this._apiUrl,
              user: this._user,
              dao: this,
            },
          })
        );
      }
      resolve(undefined);
    });
  };

  public getSystemIndex = ({
    type,
    page,
    pageSize,
  }: IGetSystemIndexParams): Promise<IIndex | undefined> => {
    return new Promise<IIndex | undefined>(async resolve => {
      if (this._user) {
        resolve(
          await getSystemIndex({
            type,
            page,
            pageSize,
            context: {
              apiUrl: this._apiUrl,
              user: this._user,
            },
          })
        );
      }
    });
  };

  /**
   * Get locations from the server
   * @param {any} params params passed from the UI layer
   * @param {Gizmo} gizmo gizmo instance that asking for the locations
   * @returns
   */
  public getLocations = async ({ params, gizmo }: IGetLocationsParams) => {
    return await getLocations({
      params,
      gizmo,
      context: {
        apiUrl: this._apiUrl,
        user: this._user,
      },
    });
  };

  /**
   * Get root gizmo configurations from a base (parent)
   *
   * @param {string} fid Flowy id of the base root gizmo config
   *
   * @returns {IGizmo[]} list of root gizmo configurations
   */
  public getRootGizmoConfigs = ({
    fid,
    publicRG,
  }: IGetRootGizmoConfigs): Promise<IGizmo[]> => {
    return new Promise<IGizmo[]>(async resolve => {
      resolve(
        await getRootGizmoConfigs({
          fid,
          publicRG,
          context: {
            apiUrl: this._apiUrl,
            user: this._user,
            database: this._database,
          },
        })
      );
    });
  };

  /**
   * Get root gizmo data from the server
   *
   * @param {string} rgfid Flowy id of the root gizmo
   * @param {string} fid Flowy id of the root gizmo data
   *
   * @returns  {IRootGizmoData[]} list of root gizmo data
   */
  public getRootGizmoData = ({
    rgfid,
    fid,
  }: IGetRootGizmoData): Promise<IRootGizmoData[]> => {
    return new Promise<IRootGizmoData[]>(async resolve => {
      resolve(
        await getRootGizmoData({
          rgfid,
          fid,
          context: {
            apiUrl: this._apiUrl,
            user: this._user,
            database: this._database,
          },
        })
      );
    });
  };

  /**
   * Download pdf populated with RootGizmoData
   *
   * @param {string} rgfid RootGizmo Flowy id
   * @param {string} id RootGizmoData database id
   * @returns
   */
  public getRootGizmoDataPdf = ({
    rgfid,
    id,
  }: IGetRootGizmoDataPdf): Promise<any> => {
    return new Promise<any>(async resolve => {
      resolve(
        await getRootGizmoDataPdf({
          rgfid,
          id,
          context: {
            apiUrl: this._apiUrl,
            user: this._user,
          },
        })
      );
    });
  };

  /**
   * Save root gizmo data on the server
   *
   * @param {IRootGizmoData[]} rootGizmoData data we're going to save on the server
   *
   * @returns {IRootGizmoData} updated root gizmo data
   */
  public saveRootGizmoData = ({
    rootGizmoData,
    publicRG,
  }: ISaveRootGizmoData): Promise<IRootGizmoData[]> => {
    return new Promise<IRootGizmoData[]>(async resolve => {
      resolve(
        await saveRootGizmoData({
          rootGizmoData,
          publicRG,
          context: {
            apiUrl: this._apiUrl,
            user: this._user,
            database: this._database,
          },
        })
      );
    });
  };

  /**
   * Remove RootGizmoData from the local storage, the function to delete from the server is
   * deleteRootGizmoData.
   *
   * @param {IRootGizmoData[]} rootGizmoData data we're going to remove from the local storage
   */
  public removeRootGizmoData = async ({
    rootGizmoData,
    publicRG,
  }: IRemoveRootGizmoData) => {
    await removeRootGizmoData({
      rootGizmoData,
      publicRG,
      context: {
        database: this._database,
        global: this._global,
      },
    });
  };

  /**
   * Get batch index file from the server
   *
   * @param {string} fid Flowy id of the RootGizmo
   * @returns
   */
  public getBatchIndex = ({ fid }: IGetBatchIndex): Promise<any> => {
    return new Promise<any>(async resolve => {
      resolve(
        await getBatchIndex({
          fid,
          context: {
            apiUrl: this._apiUrl,
            user: this._user,
          },
        })
      );
    });
  };

  /**
   * Run a server validation
   *
   * @param {IGizmo} config configuration of the gizmo that has the serverValidation
   * @param {IRootGizmoData} rootGizmoData root gizmo data that's going to be sent to the server
   *
   * @returns
   */
  public serverValidation = ({
    config,
    rootGizmoData,
  }: IServerValidation): Promise<IError[]> => {
    return new Promise<IError[]>(async resolve => {
      resolve(
        await serverValidation({
          config,
          rootGizmoData,
          context: {
            apiUrl: this._apiUrl,
            user: this._user,
          },
        })
      );
    });
  };

  /**
   * Run a server script
   *
   * @param {Gizmo} gizmo gizmo that has the server script configuration
   * @param {any} rootGizmoData root gizmo data that's going to be sent to the server
   */
  public serverScript = async ({
    gizmo,
    rootGizmoData,
  }: IServerScriptParams) => {
    await serverScript({
      gizmo,
      rootGizmoData,
      context: {
        apiUrl: this._apiUrl,
        user: this._user,
      },
    });
  };

  /**
   * Search registers by text on the server
   *
   * @param {string} text text we're going to search
   * @param {string} rootGizmoFid Flowy id of the root gizmo of the index from where we're
   *  searching
   * @param {IGizmo} gizmoConfig configuration of the gizmo that has the index
   *
   * @returns
   */
  public textSearch = ({
    rootGizmoFid,
    text,
    gizmoConfig,
  }: ITextSearch): Promise<IIndex | undefined> => {
    return new Promise<IIndex | undefined>(async resolve => {
      resolve(
        await textSearch({
          rootGizmoFid,
          text,
          gizmoConfig,
          context: {
            apiUrl: this._apiUrl,
            user: this._user,
            dao: this,
          },
        })
      );
    });
  };

  /**
   * Search registers by column on the server
   *
   * @param searchColumns object defining the search columns with its values
   * @param {string} rootGizmoFid Flowy id of the root gizmo of the index from where we're
   *  searching
   * @param {IGizmo} gizmoConfig configuration of the gizmo that has the index
   *
   * @returns
   */
  public columnSearch = ({
    searchColumns,
    rootGizmoFid,
    logicOperator,
    gizmoConfig,
  }: IColumnSearch): Promise<IIndex | undefined> => {
    return new Promise<IIndex | undefined>(async resolve => {
      return resolve(
        columnSearch({
          searchColumns,
          rootGizmoFid,
          gizmoConfig,
          logicOperator,
          context: {
            apiUrl: this._apiUrl,
            user: this._user,
            dao: this,
          },
        })
      );
    });
  };

  /**
   * Search filled forms (registers) depending on the the gizmo configuration
   * @param {IGizmo} gizmoConfig gizmo's configuration
   * @returns
   */
  public searchFilledForms = ({
    gizmoConfig,
    rootGizmoFid,
    filterSearch,
    textSearch,
    page,
    pageSize,
  }: ISearchFilledFormsParams): Promise<IIndex> => {
    return new Promise<IIndex>(async resolve => {
      try {
        resolve(
          await searchFilledForms({
            gizmoConfig,
            rootGizmoFid,
            filterSearch,
            textSearch,
            page,
            pageSize,
            context: {
              apiUrl: this._apiUrl,
              user: this._user,
            },
          })
        );
      } catch (e) {
        console.log('searchFilledForms->eeee: ', e);
      }
    });
  };

  /**
   * Get form signup root gizmo configs from the server
   *
   * @param {string} fid Flowy id of the form signup
   */
  public getFormSignup = ({ fid }: IGetFormSignup): Promise<IGizmo[]> => {
    return new Promise<IGizmo[]>(async resolve => {
      resolve(
        await getFormSignup({
          fid,
          context: {
            apiUrl: this._apiUrl,
            database: this._database,
          },
        })
      );
    });
  };
}

interface IGetIndexParams {
  type: 'rootGizmo' | 'rootGizmoData';
  /**
   * Default parameter
   */
  fid?: string;
  /**
   * Page we're going to retrieve from the server
   */
  page?: number;
  /**
   * Number of elements we're going to bring from the server
   */
  pageSize?: number;
  /**
   * Gizmo's instance
   */
  gizmo?: Gizmo;
  /**
   * Gizmo configuration we're going to use to retrieve the index
   */
  gizmoConfig?: IGizmo;
}

interface IGetSystemIndexParams {
  type: 'users';
  page?: number;
  pageSize?: number;
}

interface IGetLocationsParams {
  params: any;
  gizmo: Gizmo;
}

interface IGetRootGizmoConfigs {
  /**
   * Flowy id of the root gizmo config we're going to get from the server
   */
  fid: string;
  /**
   * If the root gizmo is public mode, we're using the word open because public is
   * a reserved word in javascript
   */
  publicRG?: boolean;
}

interface IGetRootGizmoData {
  /**
   * Root gizmo Flowy id
   */
  rgfid: string;
  /**
   * Root gizmo data Flowy id
   */
  fid: string;
}

interface IGetRootGizmoDataPdf {
  /**
   * Root gizmo Flowy id
   */
  rgfid: string;
  /**
   * Root gizmo data
   */
  id?: string;
  /**
   * Root gizmo data Flowy id
   */
  // fid: string;
  /**
   * This will be populated when we have multiple templates for the same form
   */
  // templateFid?: string;
}

interface ISaveRootGizmoData {
  /**
   * List of root gizmo data to be saved
   */
  rootGizmoData: IRootGizmoData[];
  /**
   * If the root gizmo is public mode, we're using the word open because public is
   * a reserved word in javascript
   */
  publicRG?: boolean;
}

interface IRemoveRootGizmoData {
  /**
   * List of root gizmo data to be removed
   */
  rootGizmoData: (IRootGizmoData | IFilledFormV2)[];
  /**
   * TODO:
   * Should this go???
   */
  publicRG?: boolean;
}

interface IGetBatchIndex {
  /**
   * FlowyId of the RootGizmo from which we're going to get the batch index file
   */
  fid: string;
}

interface IServerValidation {
  config: IGizmo;
  rootGizmoData: IRootGizmoData;
}

interface ITextSearch {
  text: string;
  rootGizmoFid: string;
  gizmoConfig?: IGizmo;
}

interface IColumnSearch {
  /**
   * We're going to send a list because we want to support multiple column searches
   */
  searchColumns: {
    /**
     * On the UI side they are known as selectedKeys, they will arrive as a list when users selects
     * multiple values
     */
    values: string[];
    /**
     * Flowy id of the gizmo that has the column search
     */
    gizmoFid: string;
  }[];
  logicOperator?: 'and' | 'or';
  rootGizmoFid: string;
  gizmoConfig?: IGizmo;
}

interface IGetFormSignup {
  fid: string;
}

interface ISearchFilledFormsParams {
  gizmoConfig: IGizmo;
  rootGizmoFid: string;
  filterSearch?: string;
  textSearch?: string;
  page?: number;
  pageSize?: number;
}

interface IServerScriptParams {
  gizmo: Gizmo;
  rootGizmoData: any;
}

export default Dao;
